import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './PendingApproval.css';

/**
 * Sida för användare vars Player-konto inte är 'active'.
 *
 * Sprint 2 av Fluxer-migrationen: closed-community-grinden landar nya
 * Steam-signups som 'pending'. Här kan de antingen lösa in en invite-kod
 * (auto-promotar till active om koden har auto_approve) eller vänta på
 * att en gather-manager godkänner manuellt.
 *
 * Suspended/banned visar samma sida men utan invite-input — bara info.
 */
export default function PendingApproval() {
  const { t } = useTranslation();
  const { user, isLoggedIn, isActive, accessStatus, validateInvite, redeemInvite, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-fyll invite-kod från ?invite=XXX i URL, eller från localStorage
  // (sparat innan login-redirect). Sista nödutgång: tom så användaren
  // kan klistra in själv.
  // Invite-koder är case-sensitive base64-URL-safe (secrets.token_urlsafe).
  // Bevara koden som den kommer — INGEN toUpperCase.
  const [code, setCode] = useState(() => {
    const fromUrl = searchParams.get('invite') || '';
    if (fromUrl) return fromUrl;
    return localStorage.getItem('pending_invite') || '';
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  useEffect(() => {
    if (!isLoggedIn) {
      // Spara invite-koden så den överlever Steam/Discord-OAuth-redirect.
      // AuthCallback poppar den efter inloggning och tar oss tillbaka hit.
      const invite = searchParams.get('invite');
      if (invite) localStorage.setItem('pending_invite', invite);
      navigate('/login', { replace: true });
      return;
    }
    if (isActive) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, isActive, navigate, searchParams]);

  if (!isLoggedIn || isActive) {
    return null;
  }

  const handleCheck = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await validateInvite(code.trim());
      if (result.valid) {
        const note = result.auto_approve
          ? t('auth.codeValidAuto')
          : t('auth.codeValidManual');
        setMessage({ type: 'info', text: note });
      } else {
        setMessage({ type: 'error', text: t(_readableReason(result.reason)) });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('auth.codeCheckFailed') });
    } finally {
      setBusy(false);
    }
  };

  const handleRedeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await redeemInvite(code.trim());
      if (result.ok && result.granted_active) {
        setMessage({ type: 'success', text: t('auth.redeemedActive') });
        setTimeout(() => navigate('/', { replace: true }), 1200);
      } else if (result.ok) {
        setMessage({
          type: 'info',
          text: t('auth.redeemedPending'),
        });
      } else if (result.error === 'invalid_code') {
        setMessage({ type: 'error', text: t('auth.codeInvalid') });
      } else if (result.error === 'access_revoked') {
        setMessage({ type: 'error', text: t('auth.accountSuspendedShort') });
      } else {
        setMessage({ type: 'error', text: result.error || t('auth.genericError') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('auth.redeemFailed') });
    } finally {
      setBusy(false);
    }
  };

  const isPending = accessStatus === 'pending';
  const isSuspended = accessStatus === 'suspended';
  const isBanned = accessStatus === 'banned';

  return (
    <div className="pending-page">
      <div className="pending-card">
        <span className={`pending-status-pill ${accessStatus || ''}`}>
          {t(_statusLabel(accessStatus))}
        </span>
        <h1 className="pending-title">{t(_titleFor(accessStatus))}</h1>
        <p className="pending-subtitle">
          Inloggad som <strong>{user?.steam_username || user?.discord_username || user?.username}</strong>.
        </p>
        <p className="pending-subtitle">{t(_descriptionFor(accessStatus))}</p>

        {isPending && (
          <div className="pending-form">
            <input
              className="pending-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('auth.invitePlaceholder')}
              autoComplete="off"
              maxLength={64}
              disabled={busy}
            />
            <button
              className="pending-button"
              onClick={handleRedeem}
              disabled={busy || !code.trim()}
              type="button"
            >
              {busy ? t('auth.processing') : t('auth.redeemCode')}
            </button>
            <button
              className="pending-action-link"
              onClick={handleCheck}
              disabled={busy || !code.trim()}
              type="button"
            >
              {t('auth.checkOnly')}
            </button>
          </div>
        )}

        {message && (
          <div className={`pending-message ${message.type}`}>{message.text}</div>
        )}

        <div className="pending-help">
          {isPending && (
            <>{t('auth.noInviteHint')}</>
          )}
          {(isSuspended || isBanned) && (
            <>{t('auth.contactAdminHint')}</>
          )}
        </div>

        <div className="pending-actions">
          <button className="pending-action-link" onClick={logout} type="button">
            Logga ut
          </button>
        </div>
      </div>
    </div>
  );
}

function _statusLabel(status) {
  switch (status) {
    case 'pending': return 'auth.statusPending';
    case 'suspended': return 'auth.statusSuspended';
    case 'banned': return 'auth.statusBanned';
    default: return 'auth.statusUnknown';
  }
}

function _titleFor(status) {
  switch (status) {
    case 'pending': return 'auth.titlePending';
    case 'suspended': return 'auth.titleSuspended';
    case 'banned': return 'auth.titleBanned';
    default: return 'auth.titleNone';
  }
}

function _descriptionFor(status) {
  switch (status) {
    case 'pending': return 'auth.descPending';
    case 'suspended': return 'auth.descSuspended';
    case 'banned': return 'auth.descBanned';
    default: return '';
  }
}

function _readableReason(reason) {
  switch (reason) {
    case 'not_found': return 'auth.reasonNotFound';
    case 'expired': return 'auth.reasonExpired';
    case 'exhausted': return 'auth.reasonExhausted';
    case 'inactive': return 'auth.reasonInactive';
    case 'missing_code': return 'auth.reasonMissingCode';
    default: return 'auth.codeInvalid';
  }
}
